import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import UserForm from '../../components/users/UserForm';
import { ChevronLeft, AlertTriangle } from 'lucide-react';

const UserFormPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const isEditing = !!userId;

    // 權限檢查
    useEffect(() => {
        // 非管理員不能訪問此頁面
        if (currentUser.user_type !== 'admin') {
            navigate('/dashboard');
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        // 如果是編輯模式，獲取用戶詳情
        if (isEditing) {
            fetchUserDetails();
        }
    }, [userId]);

    const fetchUserDetails = async () => {
        setIsLoading(true);
        try {
            const userData = await userService.getUserById(userId);
            setUser(userData);
        } catch (error) {
            console.error('Error fetching user details:', error);
            setErrorMessage('無法獲取用戶資訊，請稍後再試。');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    // 編輯模式但找不到用戶
    if (isEditing && !user) {
        return (
            <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-800 mb-2">找不到用戶</h2>
                <p className="text-gray-600 mb-4">無法找到ID為 {userId} 的用戶資訊。</p>
                <button
                    onClick={() => navigate('/users')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                    返回用戶列表
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={() => navigate('/users')}
                    className="p-2 mr-2 rounded-md hover:bg-gray-100"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold">
                    {isEditing ? '編輯用戶' : '新增用戶'}
                </h1>
            </div>

            {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
                    {errorMessage}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                    <UserForm
                        initialData={user}
                        isEditing={isEditing}
                    />
                </div>
            </div>
        </div>
    );
};

export default UserFormPage;